import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { BranchComparison as BranchComparisonModel } from '@/api/models';
import { BranchComparisonSkeleton } from './BranchComparisonSkeleton';
import { BranchComparisonView } from './BranchComparisonView';
import { useBranchComparison } from './useBranchComparison';

type BranchComparisonContentProps = {
    data: BranchComparisonModel | null;
    error: ErrorResponse | null;
    isLoading: boolean;
};

/**
 * Self-served branch-comparison widget — same container / view /
 * skeleton / hook split as the other Sales widgets. Fetches the live
 * `/sales/branch-comparison` endpoint, scoped to the page-level date
 * range from `useSales()`.
 */
export const BranchComparison: React.FC = () => {
    const { data, error, isLoading, onRetry } = useBranchComparison();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('sales.widgets.branchComparison.errorTitle'),
            errorMessage: t('sales.widgets.branchComparison.errorMessage'),
            errorRetry: t('sales.widgets.branchComparison.errorRetry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="branch-comparison-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="branch-comparison-error-boundary"
            onRetry={onRetry}
            resetKeys={[error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <BranchComparisonContent
                data={data}
                error={error}
                isLoading={isLoading}
            />
        </ErrorBoundary>
    );
};

const BranchComparisonContent: React.FC<BranchComparisonContentProps> = ({
    data,
    error,
    isLoading,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading || data === null) {
        return <BranchComparisonSkeleton />;
    }

    return <BranchComparisonView data={data} />;
};
